import { useState, useEffect, useMemo, useCallback } from 'react';
import { debounce, min } from 'lodash';
import qs from 'qs';
import { PartyManagerContext } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  transformToInternalSchema,
  LEAD_STATUS,
  searchInTanssForEmail,
  searchInWeclappForEmail,
  searchInCodemetaForEmail,
  searchInTanssForCustomerNumber,
  searchInWeclappForCustomerNumber,
  searchInCodemetaForCustomerNumber,
  searchInTanssForSupplierNumber,
  searchInWeclappForSupplierNumber,
  searchInCodemetaForSupplierNumber,
  SIMPLE_STATE_KEYS,
  searchInWeclappPartyById,
  searchInTanssCompanyById,
  searchInTanssEmployeeById,
} from './partyManagerUtils';
import FormField from './FormField';
import SalutationField from './SalutationField';
import DateField from './DateField';
import PartyTypeSelector from './PartyTypeSelector';
import { Box } from '@mui/system';
import { IconButton, Typography } from '@mui/material';
import PartyRoleField from './PartyRoleField';
import PartyAlert from './PartyAlert';
import ArrayField from './ArrayField';
import ParentPartySelector from './ParentPartySelector';
import api from '@/services/ApiService';
import { PartyHeader } from './PartyHeader';
import EmployeeSelector from './EmployeeSelector';
import ContactSelector from './ContactSelector';
import OrganizationAssignmentSelector from './OrganizationAssignmentSelector';
import PartyAddresses from './PartyAddresses';
import AddIcon from '@mui/icons-material/Add';
import saveParty from './saveParty';
import TargetSystemSelector from './TargetSystemSelector';
import CustomerNumberChecker from './CustomerNumberChecker';
import ResponsibleUserSelector from './ResponsibleUserSelector';
import { useRouteLoaderData } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';

const PartyManager = ({ data = null, handleClose }) => {
  const { users, deployment } = useRouteLoaderData('auth');
  const [isPartyManagerLoading, setIsPartyManagerLoading] = useState(false);
  const [currentCodemetaParty, setCurrentCodemetaParty] = useState(data);
  const [partyData, setPartyData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sourcesToHandle, setSourcesToHandle] = useState({
    codemeta: true,
    weclapp: true,
    tanss: true,
  });
  const [alert, setAlert] = useState({
    open: false,
    type: 'info',
    message: '',
  });
  const [pendingMatchConfirmation, setPendingMatchConfirmation] =
    useState(null);
  const [pendingImportConfirmation, setPendingImportConfirmation] =
    useState(null);
  const [contactWarnings, setContactWarnings] = useState({});
  const [responsibleUserWarnings, setResponsibleUserWarnings] = useState({});

  const [personOptions, setPersonOptions] = useState([]);
  const [selectedPersonValue, setSelectedPersonValue] = useState([]);
  const [isSearchingPersons, setIsSearchingPersons] = useState(false);

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrgValue, setSelectedOrgValue] = useState([]);
  const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);

  const [parentPartyMissingSources, setParentPartyMissingSources] = useState(
    [],
  );
  const [isCheckingParentParty, setIsCheckingParentParty] = useState(false);

  const [isCheckingPrivateCustomerNumber, setIsCheckingPrivateCustomerNumber] =
    useState(false);
  const [duplicateCustomerNumbers, setDuplicateCustomerNumbers] = useState([]);

  const transformArrayToStateStructure = (arr, source = 'codemeta') => {
    if (!Array.isArray(arr)) return [];
    return arr.map((value) => ({
      [source]: value,
      edit: value,
      selected: source,
    }));
  };

  useEffect(() => {
    const initializeComponent = async () => {
      const emptyFieldState = (initialValue, defaultValue, isArray = false) => {
        const value = initialValue !== undefined ? initialValue : defaultValue;
        const state = {
          codemeta: value,
          edit: value,
          selected: data?._id ? 'codemeta' : 'edit',
        };
        Object.keys(POSSIBLE_SOURCES).forEach((source) => {
          state[source] = isArray ? [] : defaultValue;
        });
        state.codemeta = value;
        return state;
      };

      let augmentedData = data ? { ...data } : null;
      if (data && data.partyType === 'PERSON' && data._id) {
        try {
          const { data: orgsData } = await api.get(
            `/party?contacts.id=${data._id}`,
          );
          const assignedOrganizations = (orgsData?.result || []).map((org) => {
            const contactInfo =
              org.contacts?.find((c) => c.id === data._id) || {};
            return { id: org._id, contact: contactInfo.contact || false };
          });
          augmentedData.assignedOrganizations = assignedOrganizations;
        } catch (error) {
          console.error('Fehler beim Laden der Firmenzugehörigkeiten:', error);
        }
      }
      const initialData = transformToInternalSchema('codemeta', augmentedData);

      const baseState = {
        id: emptyFieldState(initialData.id, null),
        partyType:
          initialData.partyType === 'PERSON' ? 'PERSON' : 'ORGANIZATION',
        salutation: emptyFieldState(initialData.salutation, ''),
        firstName: emptyFieldState(initialData.firstName, ''),
        lastName: emptyFieldState(initialData.lastName, ''),
        company: emptyFieldState(initialData.company, ''),
        company2: emptyFieldState(initialData.company2, ''),
        referenceNumber: emptyFieldState(initialData.referenceNumber, ''),
        email: emptyFieldState(initialData.email, ''),
        birthDate: emptyFieldState(initialData.birthDate, null),
        customerNumber: emptyFieldState(initialData.customerNumber, ''),
        supplierNumber: emptyFieldState(initialData.supplierNumber, ''),
        isCustomer: emptyFieldState(initialData.isCustomer, false),
        isSupplier: emptyFieldState(initialData.isSupplier, false),
        leadStatus: emptyFieldState(initialData.leadStatus, 'NONE'),
        phone: emptyFieldState(initialData.phone, ''),
        fax: emptyFieldState(initialData.fax, ''),
        website: emptyFieldState(initialData.website, ''),
        mobilePhone1: emptyFieldState(initialData.mobilePhone1, ''),
        mobilePhone2: emptyFieldState(initialData.mobilePhone2, ''),
        phoneHome: emptyFieldState(initialData.phoneHome, ''),
        departments: transformArrayToStateStructure(
          initialData.departments,
          'codemeta',
        ),
        domains: transformArrayToStateStructure(
          initialData.domains,
          'codemeta',
        ),
        parentPartyId: emptyFieldState(initialData.parentPartyId, null),
        contacts: emptyFieldState(initialData.contacts, [], true),
        assignedOrganizations: emptyFieldState(
          initialData.assignedOrganizations,
          [],
          true,
        ),
        addresses: initialData.addresses || [],
        responsibleUserId: emptyFieldState(initialData.responsibleUserId, null),
      };

      if (!baseState.addresses || baseState.addresses.length === 0) {
        baseState.addresses.push(createEmptyAddress(true));
      }

      setPartyData(baseState);
      setCurrentCodemetaParty(data);

      if (baseState.assignedOrganizations) {
        const initialAssignments =
          baseState.assignedOrganizations.codemeta || [];
        const initialOrgIds = initialAssignments
          .map((org) => org.id)
          .filter(Boolean);
        if (initialOrgIds.length > 0) {
          try {
            const { data: orgsData } = await api.get('/party', {
              params: { _id: { $in: initialOrgIds } },
              paramsSerializer: (params) =>
                qs.stringify(params, { arrayFormat: 'brackets' }),
            });
            const fetchedOrgs = orgsData.result || [];
            setSelectedOrgValue(fetchedOrgs);
            setOrganizationOptions(fetchedOrgs);
          } catch (error) {
            console.error('Could not fetch initial organizations', error);
          }
        }
      }

      if (baseState.contacts) {
        const initialContacts = baseState.contacts.codemeta || [];
        const initialContactIds = initialContacts
          .map((c) => c.id)
          .filter(Boolean);
        if (initialContactIds.length > 0) {
          try {
            const { data: contactsData } = await api.get('/party', {
              params: { _id: { $in: initialContactIds } },
              paramsSerializer: (params) =>
                qs.stringify(params, { arrayFormat: 'brackets' }),
            });
            const fetchedContacts = contactsData.result || [];
            setSelectedPersonValue(fetchedContacts);
            setPersonOptions(fetchedContacts);
          } catch (error) {
            console.error('Could not fetch initial contacts', error);
          }
        }
      }

      if (data && data._id) {
        setIsPartyManagerLoading(true);
        try {
          const { data: datasourcesRes } = await api.get(
            `/datasource?recordId=${data._id}`,
          );
          const datasources = datasourcesRes?.result || [];
          const fetchPromises = datasources.map((ds) => {
            let promise;
            switch (ds.source) {
              case 'weclapp':
                promise = searchInWeclappPartyById(ds.sourceId);
                break;
              case 'tanss':
                const isPrivateCustomer =
                  data.partyType === 'PERSON' && data.customer;

                if (
                  ds.sourceType === 'company' ||
                  data.partyType === 'ORGANIZATION' ||
                  isPrivateCustomer
                ) {
                  promise = searchInTanssCompanyById(ds.sourceId);
                } else {
                  promise = searchInTanssEmployeeById(ds.sourceId);
                }
                break;
              default:
                return Promise.resolve(null);
            }
            return promise.then((res) => ({ ...res, source: ds.source }));
          });

          const externalResultsRaw = await Promise.all(fetchPromises);
          const externalResults = externalResultsRaw.filter((r) => r && r.data);

          if (externalResults.length > 0) {
            const codemetaResult = { data: augmentedData };
            applyFoundDataToState(codemetaResult, externalResults);
          }
        } catch (error) {
          console.error('Fehler beim Laden externer Daten:', error);
          setAlert({
            open: true,
            type: 'error',
            message:
              'Die verknüpften Daten aus den Quellsystemen konnten nicht geladen werden.',
          });
        } finally {
          setIsPartyManagerLoading(false);
        }
      }
    };

    initializeComponent();
  }, [data]);

  const createEmptyAddress = (isPrimary = false) => {
    const emptyField = (defaultValue = '') => ({
      codemeta: null,
      weclapp: null,
      tanss: null,
      edit: defaultValue,
      selected: 'edit',
    });
    return {
      street1: emptyField(),
      street2: emptyField(),
      zipcode: emptyField(),
      city: emptyField(),
      countryCode: emptyField('DE'),
      id: emptyField(),
      primeAddress: isPrimary,
    };
  };

  function normalizeString(input) {
    return input
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/\bstr\./g, 'strasse')
      .replace(/\bstraße\b/g, 'strasse')
      .replace(/[^a-z0-9]/g, '');
  }

  const handleCloseAlert = () => {
    // Reset any form fields or state here
    // Set field with alert.fieldKey to empty or default value
    if (alert.fieldKey) {
      setPartyData((prev) => ({
        ...prev,
        [alert.fieldKey]: { ...prev[alert.fieldKey], edit: '' },
      }));
    }
    console.log('Alert closed:', alert, alert.fieldKey);

    setAlert({ open: false, type: 'info', message: '' });
  };

  const mergeAddresses = (existingAddresses = [], newDataBySource) => {
    const sourcePriority = ['edit', 'codemeta', 'weclapp', 'tanss'];
    const primaryPool = [];
    const secondaryPool = [];

    const createMatchKey = (addr) => {
      const street = normalizeString(addr.street1 || '');
      const zip = normalizeString(addr.zipcode || '');
      const city = normalizeString(addr.city || '');
      if (!street && !zip && !city) return null;
      return `${street}-${zip}-${city}`;
    };
    existingAddresses.forEach((addr) => {
      const editAddr = {
        street1: addr.street1?.edit,
        street2: addr.street2?.edit,
        zipcode: addr.zipcode?.edit,
        city: addr.city?.edit,
        countryCode: addr.countryCode?.edit,
        primeAddress: addr.primeAddress,
        source: 'edit',
      };
      if (addr.primeAddress) {
        primaryPool.push(editAddr);
      } else {
        secondaryPool.push(editAddr);
      }
    });
    Object.keys(newDataBySource).forEach((source) => {
      newDataBySource[source]?.addresses?.forEach((addr) => {
        const sourceAddr = { ...addr, source };
        if (addr.primeAddress) {
          primaryPool.push(sourceAddr);
        } else {
          secondaryPool.push(sourceAddr);
        }
      });
    });

    let finalAddresses = [];
    if (primaryPool.length > 0) {
      const unifiedPrimary = createEmptyAddress(true);
      console.log('Primary Pool:', primaryPool);
      console.log('unifiedPrimary before:', unifiedPrimary);
      primaryPool.forEach((addr) => {
        Object.keys(unifiedPrimary).forEach((fieldKey) => {
          if (fieldKey !== 'primeAddress' && addr[fieldKey] !== undefined) {
            unifiedPrimary[fieldKey][addr.source] = addr[fieldKey];
          }
        });
      });
      Object.keys(unifiedPrimary).forEach((fieldKey) => {
        if (fieldKey !== 'primeAddress') {
          const field = unifiedPrimary[fieldKey];
          field.edit = field.edit || '';
          const selectedSource = sourcePriority.find(
            (s) =>
              field[s] !== null && field[s] !== undefined && field[s] !== '',
          );
          field.selected = selectedSource || 'edit';
        }
      });
      console.log(unifiedPrimary);
      const weclappId = primaryPool.find(
        (addr) => addr.source === 'weclapp',
      )?.id;
      console.log('Weclapp ID:', weclappId, primaryPool);
      if (weclappId) {
        unifiedPrimary.id = weclappId;
      }
      finalAddresses.push(unifiedPrimary);
    }
    const secondaryGroups = new Map();
    secondaryPool.forEach((addr) => {
      const key = createMatchKey(addr);
      if (!key) return;
      if (!secondaryGroups.has(key)) {
        secondaryGroups.set(key, []);
      }
      secondaryGroups.get(key).push(addr);
    });

    secondaryGroups.forEach((groupAddresses) => {
      const unifiedSecondary = createEmptyAddress(false);
      groupAddresses.forEach((addr) => {
        Object.keys(unifiedSecondary).forEach((fieldKey) => {
          if (fieldKey !== 'primeAddress' && addr[fieldKey] !== undefined) {
            unifiedSecondary[fieldKey][addr.source] = addr[fieldKey];
          }
        });
      });

      Object.keys(unifiedSecondary).forEach((fieldKey) => {
        if (fieldKey !== 'primeAddress') {
          const field = unifiedSecondary[fieldKey];
          field.edit = field.edit || '';

          const selectedSource = sourcePriority.find(
            (s) =>
              field[s] !== null && field[s] !== undefined && field[s] !== '',
          );
          field.selected = selectedSource || 'edit';
        }
      });
      finalAddresses.push(unifiedSecondary);
    });
    if (primaryPool.length === 0 && finalAddresses.length > 0) {
      finalAddresses[0].primeAddress = true;
    }
    if (finalAddresses.length === 0) {
      finalAddresses.push(createEmptyAddress(true));
    }
    console.log(finalAddresses);
    return finalAddresses;
  };

  const applyFoundDataToState = (codemetaResult, externalResults) => {
    const newCodemetaData = transformToInternalSchema(
      'codemeta',
      codemetaResult.data,
    );
    const externalData = {};
    const newWarnings = {};
    const newResponsibleUserWarnings = {};

    externalResults.forEach((result) => {
      externalData[result.source] = transformToInternalSchema(
        result.source,
        result.data,
      );
      if (result.warnings?.length > 0) {
        newWarnings[result.source] = result.warnings;
      }
      if (result.responsibleUserWarnings?.length > 0) {
        newResponsibleUserWarnings[result.source] =
          result.responsibleUserWarnings;
      }
    });
    setContactWarnings(newWarnings);
    setResponsibleUserWarnings(newResponsibleUserWarnings);

    setPartyData((prevData) => {
      const buildField = (key, defaultValue) => ({
        edit: prevData[key]?.edit,
        codemeta: newCodemetaData[key] ?? defaultValue,
        selected: prevData[key]?.selected || 'codemeta',
        weclapp: externalData.weclapp?.[key] ?? defaultValue,
        tanss: externalData.tanss?.[key] ?? defaultValue,
      });

      const buildArrayField = (key) => ({
        edit: prevData[key]?.edit,
        codemeta: newCodemetaData[key] || [],
        selected: prevData[key]?.selected || 'codemeta',
        weclapp: externalData.weclapp?.[key] || [],
        tanss: externalData.tanss?.[key] || [],
      });

      const mergeDepartments = (existingDepts = [], newCodemetaDepts = []) => {
        if (!newCodemetaDepts || newCodemetaDepts.length === 0)
          return existingDepts;

        const mergedMap = new Map();
        existingDepts.forEach((item) => {
          mergedMap.set((item.edit || '').toLowerCase(), { ...item });
        });

        newCodemetaDepts.forEach((codemetaName) => {
          const key = (codemetaName || '').toLowerCase();
          if (!key) return;

          if (mergedMap.has(key)) {
            const existingItem = mergedMap.get(key);
            const updatedItem = {
              ...existingItem,
              codemeta: codemetaName,
              selected: 'codemeta',
            };
            mergedMap.set(key, updatedItem);
          } else {
            const newItem = {
              codemeta: codemetaName,
              edit: codemetaName,
              selected: 'codemeta',
            };
            mergedMap.set(key, newItem);
          }
        });
        return Array.from(mergedMap.values());
      };

      const mergeDomains = (existingDomains = [], newCodemetaDomains = []) => {
        if (!newCodemetaDomains || newCodemetaDomains.length === 0)
          return existingDomains;

        const mergedMap = new Map();
        existingDomains.forEach((item) => {
          mergedMap.set((item.edit || '').toLowerCase(), { ...item });
        });

        newCodemetaDomains.forEach((codemetaName) => {
          const key = (codemetaName || '').toLowerCase();
          if (!key) return;

          if (mergedMap.has(key)) {
            const existingItem = mergedMap.get(key);
            const updatedItem = {
              ...existingItem,
              codemeta: codemetaName,
              selected: 'codemeta',
            };
            mergedMap.set(key, updatedItem);
          } else {
            const newItem = {
              codemeta: codemetaName,
              edit: codemetaName,
              selected: 'codemeta',
            };
            mergedMap.set(key, newItem);
          }
        });
        return Array.from(mergedMap.values());
      };

      const newState = {
        id: buildField('id', null),
        partyType:
          newCodemetaData.partyType || prevData.partyType || 'ORGANIZATION',
        salutation: buildField('salutation', ''),
        firstName: buildField('firstName', ''),
        lastName: buildField('lastName', ''),
        company: buildField('company', ''),
        company2: buildField('company2', ''),
        referenceNumber: buildField('referenceNumber', ''),
        email: buildField('email', ''),
        birthDate: buildField('birthDate', null),
        customerNumber: buildField('customerNumber', ''),
        supplierNumber: buildField('supplierNumber', ''),
        isCustomer: buildField('isCustomer', false),
        isSupplier: buildField('isSupplier', false),
        leadStatus: buildField('leadStatus', 'NONE'),
        phone: buildField('phone', ''),
        fax: buildField('fax', ''),
        website: buildField('website', ''),
        mobilePhone1: buildField('mobilePhone1', ''),
        mobilePhone2: buildField('mobilePhone2', ''),
        phoneHome: buildField('phoneHome', ''),
        departments: mergeDepartments(
          prevData.departments,
          newCodemetaData.departments,
        ),
        domains: mergeDomains(prevData.domains, newCodemetaData.domains),
        parentPartyId: buildField('parentPartyId', null),
        contacts: buildArrayField('contacts'),
        assignedOrganizations: buildArrayField('assignedOrganizations'),
        addresses: mergeAddresses(prevData.addresses, {
          codemeta: newCodemetaData,
          weclapp: externalData.weclapp,
          tanss: externalData.tanss,
        }),
        responsibleUserId: buildField('responsibleUserId', null),
      };

      Object.keys(newState).forEach((key) => {
        if (
          newState[key] &&
          typeof newState[key] === 'object' &&
          'selected' in newState[key]
        ) {
          const currentSelectionValue = newState[key][newState[key].selected];
          if (
            currentSelectionValue === null ||
            currentSelectionValue === '' ||
            (Array.isArray(currentSelectionValue) &&
              currentSelectionValue.length === 0)
          ) {
            const firstAvailableSource = [
              'codemeta',
              'weclapp',
              'tanss',
              'edit',
            ].find((source) => {
              const val = newState[key][source];
              return (
                val !== null &&
                val !== '' &&
                (!Array.isArray(val) || val.length > 0)
              );
            });
            if (firstAvailableSource) {
              newState[key].selected = firstAvailableSource;
            }
          }
        }
      });

      if (newState.leadStatus?.weclapp === 'CONVERTED') {
        console.log('Setting leadStatus to weclapp');
        newState.leadStatus.selected = 'weclapp';
      }

      return newState;
    });

    setCurrentCodemetaParty(codemetaResult.data);
  };

  const handleConfirmMatch = () => {
    if (!pendingMatchConfirmation) return;
    const { codemetaResult, externalResults } = pendingMatchConfirmation;
    applyFoundDataToState(codemetaResult, externalResults);
    setPendingMatchConfirmation(null);
  };

  const handleCancelMatch = () => {
    if (!pendingMatchConfirmation) return;
    const { fieldKey } = pendingMatchConfirmation;
    setPartyData((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], edit: '' },
    }));
    setPendingMatchConfirmation(null);
  };

  const handleConfirmImport = () => {
    if (!pendingImportConfirmation) return;
    const { externalResults } = pendingImportConfirmation;

    const importedDataBySource = {};
    const newWarnings = {};
    const newResponsibleUserWarnings = {};
    externalResults.forEach((result) => {
      if (result.data) {
        const transformedData = transformToInternalSchema(
          result.source,
          result.data,
        );
        importedDataBySource[result.source] = transformedData;
        if (result.warnings?.length > 0) {
          newWarnings[result.source] = result.warnings;
        }
        if (result.responsibleUserWarnings?.length > 0) {
          newResponsibleUserWarnings[result.source] =
            result.responsibleUserWarnings;
        }
      }
    });
    setContactWarnings(newWarnings);
    setResponsibleUserWarnings(newResponsibleUserWarnings);

    setPartyData((prevData) => {
      const updatedData = JSON.parse(JSON.stringify(prevData));
      const firstSourceWithData =
        externalResults.find((r) => r.data)?.source || 'edit';

      Object.keys(importedDataBySource).forEach((source) => {
        const sourceData = importedDataBySource[source];
        Object.keys(sourceData).forEach((fieldKey) => {
          if (fieldKey === 'addresses') return;
          if (SIMPLE_STATE_KEYS.includes(fieldKey)) {
            if (source === firstSourceWithData) {
              updatedData[fieldKey] = sourceData[fieldKey];
            }
          } else if (updatedData[fieldKey]) {
            updatedData[fieldKey][source] = sourceData[fieldKey];
            if (source === firstSourceWithData) {
              updatedData[fieldKey].selected = source;
              if (
                updatedData[fieldKey].edit === '' ||
                updatedData[fieldKey].edit === null
              ) {
                updatedData[fieldKey].edit = sourceData[fieldKey];
              }
            }
          }
        });
      });

      if (updatedData.leadStatus?.weclapp === 'CONVERTED') {
        updatedData.leadStatus.selected = 'weclapp';
      }

      const contactField = updatedData.contacts;
      if (contactField) {
        const currentSelectedSource = contactField.selected || 'edit';
        const currentContacts = contactField[currentSelectedSource];
        if (!Array.isArray(currentContacts) || currentContacts.length === 0) {
          const sourcePriority = ['codemeta', 'weclapp', 'tanss'];
          let newSelectedSource = 'edit';
          for (const source of sourcePriority) {
            const contactsFromSource = contactField[source];
            if (
              Array.isArray(contactsFromSource) &&
              contactsFromSource.length > 0
            ) {
              newSelectedSource = source;
              break;
            }
          }
          contactField.selected = newSelectedSource;
        }
      }

      updatedData.addresses = mergeAddresses(
        prevData.addresses,
        importedDataBySource,
      );
      return updatedData;
    });

    setPendingImportConfirmation(null);
  };

  const handleCancelImport = () => {
    if (!pendingImportConfirmation) return;
    const { fieldKey } = pendingImportConfirmation;
    setPartyData((prev) => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], edit: '' },
    }));
    setPendingImportConfirmation(null);
  };

  const handleRoleChange = (roleKey, value) => {
    setPartyData((prev) => ({
      ...prev,
      [roleKey]: {
        ...prev[roleKey],
        edit: value,
        selected: 'edit',
      },
    }));
  };

  const mergeDataIntoState = (newDataBySource) => {
    const newWarnings = {};
    Object.keys(newDataBySource).forEach((source) => {
      if (newDataBySource[source].warnings?.length > 0) {
        newWarnings[source] = newDataBySource[source].warnings;
      }
    });
    setContactWarnings((prev) => ({ ...prev, ...newWarnings }));
    setResponsibleUserWarnings((prev) => ({
      ...prev,
      ...newDataBySource.responsibleUserWarnings,
    }));

    setPartyData((prevData) => {
      if (!prevData) return null;
      const updatedData = { ...prevData };
      Object.keys(newDataBySource).forEach((source) => {
        const sourceData = newDataBySource[source];
        Object.keys(sourceData).forEach((fieldKey) => {
          if (fieldKey === 'addresses') return;
          if (updatedData[fieldKey]) {
            if (SIMPLE_STATE_KEYS.includes(fieldKey)) {
              updatedData[fieldKey] = sourceData[fieldKey];
            } else {
              updatedData[fieldKey] = {
                ...updatedData[fieldKey],
                [source]: sourceData[fieldKey],
              };
            }
          }
        });
      });
      updatedData.addresses = mergeAddresses(
        prevData.addresses,
        newDataBySource,
      );
      return updatedData;
    });
  };

  const handleFieldChange = (fieldKey, value, isBlur = false) => {
    setPartyData((prevData) => {
      if (!prevData) return prevData;
      const newPartyData = { ...prevData };
      const fieldState = prevData[fieldKey];
      newPartyData[fieldKey] = {
        ...fieldState,
        edit: value,
        selected: 'edit',
      };
      if (fieldKey === 'customerNumber') {
        newPartyData.isCustomer.edit = !!value;
        newPartyData.isCustomer.selected = 'edit';
      }
      if (fieldKey === 'supplierNumber') {
        newPartyData.isSupplier.edit = !!value;
        newPartyData.isSupplier.selected = 'edit';
      }
      return newPartyData;
    });

    if (isBlur && value) {
      const currentPartyType = partyData.partyType;
      const searchForPerson =
        currentPartyType === 'PERSON' && fieldKey === 'email';
      const searchForOrganization =
        currentPartyType === 'ORGANIZATION' &&
        (fieldKey === 'customerNumber' || fieldKey === 'supplierNumber');

      if (searchForPerson || searchForOrganization) {
        handleSearchForMatches(value, fieldKey);
      }

      if (currentPartyType === 'PERSON' && fieldKey === 'customerNumber') {
        checkCustomerNumberUniqueness(value);
      }
    }
  };

  const checkCustomerNumberUniqueness = async (customerNumber) => {
    if (!customerNumber) return;
    setIsCheckingPrivateCustomerNumber(true);
    const duplicates = [];
    const tryAdd = async (fn, source) =>
      fn(customerNumber)
        .then(
          (res) =>
            res?.data?.id && duplicates.push({ sourceId: res.data.id, source }),
        )
        .catch(console.error);
    try {
      await tryAdd(searchInCodemetaForCustomerNumber, 'codemeta');
      await tryAdd(searchInWeclappForCustomerNumber, 'weclapp');
      await tryAdd(searchInTanssForCustomerNumber, 'tanss');
      setDuplicateCustomerNumbers(duplicates);
    } catch {
      setDuplicateCustomerNumbers([]);
    } finally {
      setIsCheckingPrivateCustomerNumber(false);
    }
  };

  const handleSearchForMatches = async (searchTerm, fieldKey) => {
    setIsPartyManagerLoading(true);
    let searchPromises = [];

    switch (fieldKey) {
      case 'email':
        searchPromises = [
          searchInCodemetaForEmail(searchTerm),
          searchInWeclappForEmail(searchTerm),
          searchInTanssForEmail(searchTerm),
        ];
        break;
      case 'customerNumber':
        searchPromises = [
          searchInCodemetaForCustomerNumber(searchTerm),
          searchInWeclappForCustomerNumber(searchTerm),
          searchInTanssForCustomerNumber(searchTerm),
        ];
        break;
      case 'supplierNumber':
        searchPromises = [
          searchInCodemetaForSupplierNumber(searchTerm),
          searchInWeclappForSupplierNumber(searchTerm),
          searchInTanssForSupplierNumber(searchTerm),
        ];
        break;
      default:
        setIsPartyManagerLoading(false);
        return;
    }
    const results = await Promise.all(
      searchPromises.map((p, i) =>
        p.then((res) => ({
          ...res,
          source: ['codemeta', 'weclapp', 'tanss'][i],
        })),
      ),
    );
    setIsPartyManagerLoading(false);

    if (results.length === 0) {
      setTimeout(() => {
        const el = document.querySelector('#salutation-select');
        el?.focus();
      }, 20);
    }

    const errors = results
      .filter((r) => r?.error)
      .map((r) => <Typography key={r.source}>{r.error}</Typography>);

    if (errors.length > 0) {
      setAlert({
        open: true,
        type: 'error',
        fieldKey,
        message: <Box>{errors}</Box>,
      });
      return;
    }

    const codemetaResult = results.find(
      (r) => r.source === 'codemeta' && r.data,
    );
    const externalResults = results.filter(
      (r) => r.source !== 'codemeta' && r.data,
    );

    // Check if external results might be matched to other codemeta parties
    if (externalResults.length > 0) {
      for (const res of externalResults) {
        const { data: datasources } = await api.get(
          `/datasource?sourceId=${res.data.id}&source=${res.source}`,
        );

        if (datasources?.result?.length > 1) {
          setAlert({
            open: true,
            type: 'error',
            fieldKey,
            message: (
              <Box>
                Die Firma mit der {res.source}-ID {res.data.id} ist bereits mit
                mehreren Datensätzen in Codemeta verknüpft. Bitte lösen Sie den
                Konflikt manuell.{' '}
                {datasources.result.map((ds, index) => (
                  <>
                    <a
                      href={`/crm/parties/${ds.recordId}`}
                      key={ds.recordId}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline' }}
                    >
                      Kontakt {index + 1}
                    </a>
                    {index !== datasources.result.length - 1 ? ', ' : ''}
                  </>
                ))}
              </Box>
            ),
          });
          return;
        } else if (datasources?.result?.length === 1) {
          const existingParty = datasources.result[0];
          if (existingParty.recordId !== codemetaResult?.data?._id) {
            setAlert({
              open: true,
              type: 'error',
              fieldKey,
              message: (
                <Box>
                  Die {res.source}-ID {res.data.id} ist bereits mit einer{' '}
                  <a
                    href={`/crm/parties/${existingParty.recordId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    Firma
                  </a>{' '}
                  mit einer anderen Kundennummer verknüpft. Bitte prüfen Sie,
                  wieso es zu doppelten Kundennummern kommt.
                </Box>
              ),
            });
            return;
          }
        }
      }
    }

    if (
      currentCodemetaParty &&
      codemetaResult &&
      currentCodemetaParty.id !== codemetaResult.data.id
    ) {
      setAlert({
        open: true,
        type: 'error',
        fieldKey,
        message: (
          <Box>
            {fieldKey === 'customerNumber'
              ? 'Es wurde eine Firma mit dieser Kundennummer gefunden, die nicht mit der Firma der Lieferantennummer übereinstimmt. Die letzte Eingabe wird rückgängig gemacht.'
              : 'Es wurde eine Firma mit dieser Lieferantennummer gefunden, die nicht mit der Firma der Kundennummer übereinstimmt. Die letzte Eingabe wird rückgängig gemacht.'}
          </Box>
        ),
      });
      setPartyData((prev) => ({
        ...prev,
        [fieldKey]: { ...prev[fieldKey], edit: '' },
      }));
      return;
    }

    if (codemetaResult && !currentCodemetaParty) {
      setPendingMatchConfirmation({
        codemetaResult,
        externalResults,
        fieldKey,
      });
    } else if (
      !codemetaResult &&
      externalResults.length > 0 &&
      !currentCodemetaParty
    ) {
      setPendingImportConfirmation({ externalResults, fieldKey });
    } else if (externalResults.length > 0 || codemetaResult) {
      const dataToMerge = {};
      results.forEach((res) => {
        if (res.data) {
          dataToMerge[res.source] = {
            ...transformToInternalSchema(res.source, res.data),
            warnings: res.warnings,
          };
        }
      });
      mergeDataIntoState(dataToMerge);
    }
  };

  const getAvailableSources = (fieldKey) => {
    if (!partyData || !partyData[fieldKey]) return ['edit'];

    const available = [];
    const fieldState = partyData[fieldKey];

    if (
      SIMPLE_STATE_KEYS.includes(fieldKey) ||
      fieldKey === 'departments' ||
      fieldKey === 'domains'
    ) {
      return ['edit'];
    }

    const hasValue = (val) =>
      val !== null &&
      val !== undefined &&
      val !== '' &&
      (!Array.isArray(val) || val.length > 0);

    if (hasValue(fieldState.codemeta)) available.push('codemeta');
    Object.keys(POSSIBLE_SOURCES).forEach((source) => {
      if (hasValue(fieldState[source])) available.push(source);
    });
    available.push('edit');

    return [...new Set(available)];
  };

  const handleCycleSource = (fieldKey) => {
    setPartyData((prevData) => {
      if (!prevData) return prevData;
      const fieldState = prevData[fieldKey];
      const cycleOrder = getAvailableSources(fieldKey);
      if (cycleOrder.length <= 1) return prevData;
      const currentIndex = cycleOrder.indexOf(fieldState.selected);
      const nextIndex = (currentIndex + 1) % cycleOrder.length;
      const nextSource = cycleOrder[nextIndex];
      const updatedFieldState = { ...fieldState, selected: nextSource };
      return { ...prevData, [fieldKey]: updatedFieldState };
    });
  };

  const handleEmployeeChange = (newEmployees) => {
    setSelectedPersonValue(newEmployees);
    setPartyData((prev) => {
      const contactsField = prev.contacts;
      const currentContacts = contactsField.edit || [];

      const newContactList = newEmployees.map((emp) => {
        const existing = currentContacts.find((c) => c.id === emp._id);
        return existing || { id: emp._id, contact: false, primary: false };
      });

      return {
        ...prev,
        contacts: {
          ...contactsField,
          edit: newContactList,
          selected: 'edit',
        },
      };
    });
  };

  const handleContactChange = (newContacts) => {
    setPartyData((prev) => {
      const contactsField = prev.contacts;
      const currentContacts = contactsField.edit || [];
      const newContactIds = new Set(newContacts.map((c) => c._id));

      const updatedContactList = currentContacts.map((c) => ({
        ...c,
        contact: newContactIds.has(c.id),
      }));

      const primaryContact = updatedContactList.find((c) => c.primary);
      if (primaryContact && !primaryContact.contact) {
        primaryContact.primary = false;
      }

      return {
        ...prev,
        contacts: {
          ...contactsField,
          edit: updatedContactList,
          selected: 'edit',
        },
      };
    });
  };

  const handlePrimaryChange = (primaryId) => {
    setPartyData((prev) => {
      const contactsField = prev.contacts;
      const currentContacts = contactsField.edit || [];
      const isCurrentlyPrimary = currentContacts.find(
        (c) => c.id === primaryId,
      )?.primary;

      const updatedContactList = currentContacts.map((c) => ({
        ...c,
        primary: c.id === primaryId ? !isCurrentlyPrimary : false,
      }));

      return {
        ...prev,
        contacts: {
          ...contactsField,
          edit: updatedContactList,
          selected: 'edit',
        },
      };
    });
  };

  const handleTransferContactsToEdit = () => {
    setPartyData((prevData) => {
      const fieldKey = 'contacts';
      const fieldState = prevData[fieldKey];
      if (!fieldState || fieldState.selected === 'edit') {
        return prevData;
      }
      const dataToTransfer = fieldState[fieldState.selected] || [];
      return {
        ...prevData,
        [fieldKey]: {
          ...fieldState,
          edit: [...dataToTransfer],
          selected: 'edit',
        },
      };
    });
  };

  const handleOrganizationSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setOrganizationOptions(selectedOrgValue);
        setIsSearchingOrgs(false);
        return;
      }

      setIsSearchingOrgs(true);

      const searchWords = searchTerm
        .split(' ')
        .filter((word) => word.length > 0);

      const shouldClauses = [];

      if (searchWords.length > 0) {
        const companyMustClauses = searchWords.map((word) => ({
          text: {
            query: word,
            path: 'company',
            fuzzy: {
              maxEdits: 1,
            },
          },
        }));

        shouldClauses.push({
          compound: {
            must: companyMustClauses,
          },
        });
      }

      shouldClauses.push({
        text: {
          query: searchTerm,
          path: 'customerNumber',
          fuzzy: {
            maxEdits: 1,
          },
        },
      });
      shouldClauses.push({
        text: {
          query: searchTerm,
          path: 'supplierNumber',
          fuzzy: {
            maxEdits: 1,
          },
        },
      });

      try {
        const params = {
          search: {
            compound: {
              must: [
                {
                  text: {
                    path: 'partyType',
                    query: 'ORGANIZATION',
                  },
                },
              ],
              should: shouldClauses,
              minimumShouldMatch: 1,
            },
          },
          limit: 20,
        };

        const { data: searchData } = await api.get('/party', {
          params,
          paramsSerializer: (params) => {
            return qs.stringify(params, { arrayFormat: 'indices' });
          },
        });

        const currentSelectedIds = new Set(
          selectedOrgValue.map((org) => org._id),
        );
        const newOptions = (searchData.result || []).filter(
          (org) => !currentSelectedIds.has(org._id),
        );

        setOrganizationOptions([...selectedOrgValue, ...newOptions]);
      } catch (error) {
        console.error('Error searching for organizations:', error);
        setOrganizationOptions(selectedOrgValue);
      } finally {
        setIsSearchingOrgs(false);
      }
    }, 300),
    [selectedOrgValue],
  );

  const handlePersonSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setPersonOptions(selectedPersonValue);
        setIsSearchingPersons(false);
        return;
      }

      setIsSearchingPersons(true);
      try {
        const params = {
          search: {
            compound: {
              must: [
                {
                  text: {
                    path: 'partyType',
                    query: 'PERSON',
                  },
                },
              ],
              should: [
                {
                  text: {
                    query: searchTerm,
                    path: 'firstName',
                    fuzzy: {
                      maxEdits: 2,
                    },
                  },
                },
                {
                  text: {
                    query: searchTerm,
                    path: 'lastName',
                    fuzzy: {
                      maxEdits: 2,
                    },
                  },
                },
                {
                  text: {
                    query: searchTerm,
                    path: 'email',
                    fuzzy: {
                      maxEdits: 2,
                    },
                  },
                },
              ],
              minimumShouldMatch: 1,
            },
          },
          limit: 20,
        };

        const { data: searchData } = await api.get('/party', {
          params,
          paramsSerializer: (params) => {
            return qs.stringify(params, { arrayFormat: 'indices' });
          },
        });

        const currentSelectedIds = new Set(
          selectedPersonValue.map((person) => person._id),
        );
        const newOptions = (searchData.result || []).filter(
          (person) => !currentSelectedIds.has(person._id),
        );

        setPersonOptions([...selectedPersonValue, ...newOptions]);
      } catch (error) {
        console.error('Error searching for persons:', error);
        setPersonOptions(selectedPersonValue);
      } finally {
        setIsSearchingPersons(false);
      }
    }, 300),
    [selectedPersonValue],
  );

  const handleOrganizationAssignmentChange = (newOrgs) => {
    setSelectedOrgValue(newOrgs);
    setPartyData((prev) => {
      const field = prev.assignedOrganizations;
      const currentAssignments = field.edit || [];

      const newAssignments = newOrgs.map((org) => {
        const existing = currentAssignments.find((a) => a.id === org._id);
        return existing || { id: org._id, contact: false };
      });

      return {
        ...prev,
        assignedOrganizations: {
          ...field,
          edit: newAssignments,
          selected: 'edit',
        },
      };
    });
  };

  const handleOrganizationContactToggle = (orgId) => {
    setPartyData((prev) => {
      const field = prev.assignedOrganizations;
      const currentAssignments = field.edit || [];

      const newAssignments = currentAssignments.map((a) =>
        a.id === orgId ? { ...a, contact: !a.contact } : a,
      );

      const isAnyContactSelected = newAssignments.some((a) => a.contact);
      setSourcesToHandle((prevSources) => ({
        ...prevSources,
        weclapp: isAnyContactSelected,
      }));

      return {
        ...prev,
        assignedOrganizations: {
          ...field,
          edit: newAssignments,
          selected: 'edit',
        },
      };
    });
  };

  const handleTransferOrganizationsToEdit = () => {
    setPartyData((prevData) => {
      const fieldKey = 'assignedOrganizations';
      const fieldState = prevData[fieldKey];
      if (!fieldState || fieldState.selected === 'edit') {
        return prevData;
      }
      const dataToTransfer = fieldState[fieldState.selected] || [];
      return {
        ...prevData,
        [fieldKey]: {
          ...fieldState,
          edit: [...dataToTransfer],
          selected: 'edit',
        },
      };
    });
  };

  const getDisplayValue = (fieldKey) => {
    const field = partyData[fieldKey];
    if (!field) return undefined;
    if (SIMPLE_STATE_KEYS.includes(fieldKey)) return field;
    if (fieldKey === 'departments') return field;
    if (fieldKey === 'domains') return field;

    if (fieldKey === 'contacts' || fieldKey === 'assignedOrganizations') {
      return field.selected === 'edit'
        ? field.edit
        : field[field.selected] || [];
    }

    return field.selected === 'edit' ? field.edit : field[field.selected];
  };

  useEffect(() => {
    if (!partyData) {
      return;
    }
    const field = partyData['parentPartyId'];
    const parentPartyId = field
      ? field.selected === 'edit'
        ? field.edit
        : field[field.selected]
      : null;
    checkParentPartySources(parentPartyId);
  }, [partyData?.parentPartyId, sourcesToHandle]);

  useEffect(() => {
    const fetchMissingOrgDetails = async () => {
      if (!partyData?.assignedOrganizations) return;

      const currentSource = partyData.assignedOrganizations.selected;
      const currentAssignments =
        partyData.assignedOrganizations[currentSource] || [];
      const orgIds = currentAssignments.map((a) => a.id).filter(Boolean);

      if (orgIds.length === 0) {
        return;
      }

      const existingIds = new Set(selectedOrgValue.map((org) => org._id));
      const missingIds = orgIds.filter((id) => !existingIds.has(id));

      if (missingIds.length > 0) {
        try {
          const { data: orgsData } = await api.get('/party', {
            params: { _id: { $in: missingIds } },
            paramsSerializer: (params) =>
              qs.stringify(params, { arrayFormat: 'brackets' }),
          });

          const fetchedOrgs = orgsData.result || [];

          setSelectedOrgValue((prev) => {
            const combined = [...prev, ...fetchedOrgs];
            const unique = Array.from(
              new Map(combined.map((item) => [item._id, item])).values(),
            );
            return unique;
          });

          setOrganizationOptions((prev) => {
            const combined = [...prev, ...fetchedOrgs];
            const unique = Array.from(
              new Map(combined.map((item) => [item._id, item])).values(),
            );
            return unique;
          });
        } catch (error) {
          console.error(
            'Fehler beim Nachladen der Firmen-Details nach Import:',
            error,
          );
        }
      }
    };

    fetchMissingOrgDetails();
  }, [partyData?.assignedOrganizations]);

  const handleAddAddress = () => {
    const emptyField = (defaultValue = '') => ({
      codemeta: null,
      weclapp: null,
      tanss: null,
      edit: defaultValue,
      selected: 'edit',
    });

    setPartyData((prev) => {
      const newAddress = {
        street1: emptyField(),
        street2: emptyField(),
        zipcode: emptyField(),
        city: emptyField(),
        countryCode: emptyField('DE'),
        primeAddress: prev.addresses.length === 0,
      };
      return { ...prev, addresses: [...prev.addresses, newAddress] };
    });
  };

  const handleRemoveAddress = (index) => {
    setPartyData((prev) => {
      const updatedAddresses = prev.addresses.filter((_, i) => i !== index);
      const wasPrimary = prev.addresses[index]?.primeAddress;
      if (
        wasPrimary &&
        updatedAddresses.length > 0 &&
        !updatedAddresses.some((a) => a.primeAddress)
      ) {
        updatedAddresses[0].primeAddress = true;
      }
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handleAddressPrimaryChange = (index) => {
    setPartyData((prev) => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) => ({
        ...addr,
        primeAddress: i === index,
      })),
    }));
  };

  const handleAddressFieldChange = (addressIndex, fieldKey, value) => {
    setPartyData((prev) => {
      const newAddresses = [...prev.addresses];
      newAddresses[addressIndex] = {
        ...newAddresses[addressIndex],
        [fieldKey]: {
          ...newAddresses[addressIndex][fieldKey],
          edit: value,
          selected: 'edit',
        },
      };
      return { ...prev, addresses: newAddresses };
    });
  };

  const getAvailableAddressSources = (addressIndex, fieldKey) => {
    const field = partyData.addresses[addressIndex]?.[fieldKey];
    if (!field) return ['edit'];

    const available = [];
    const hasValue = (val) => val !== null && val !== undefined && val !== '';

    if (hasValue(field.codemeta)) available.push('codemeta');
    Object.keys(POSSIBLE_SOURCES).forEach((source) => {
      if (hasValue(field[source])) available.push(source);
    });
    available.push('edit');

    return [...new Set(available)];
  };

  const handleAddressCycleSource = (addressIndex, fieldKey) => {
    setPartyData((prev) => {
      const fieldState = prev.addresses[addressIndex][fieldKey];
      const cycleOrder = getAvailableAddressSources(addressIndex, fieldKey);
      if (cycleOrder.length <= 1) return prev;

      const currentIndex = cycleOrder.indexOf(fieldState.selected);
      const nextIndex = (currentIndex + 1) % cycleOrder.length;
      const nextSource = cycleOrder[nextIndex];

      const newAddresses = [...prev.addresses];
      newAddresses[addressIndex] = {
        ...newAddresses[addressIndex],
        [fieldKey]: { ...fieldState, selected: nextSource },
      };
      return { ...prev, addresses: newAddresses };
    });
  };

  const handleSourceToggle = (sourceKey) => {
    setSourcesToHandle((prev) => ({
      ...prev,
      [sourceKey]: !prev[sourceKey],
    }));
  };

  const checkParentPartySources = async (partyId) => {
    if (!partyId) {
      setParentPartyMissingSources([]);
      return;
    }

    setIsCheckingParentParty(true);
    const activeSources = Object.keys(sourcesToHandle).filter(
      (key) => sourcesToHandle[key],
    );

    if (activeSources.length === 0) {
      setParentPartyMissingSources([]);
      setIsCheckingParentParty(false);
      return;
    }

    const promises = activeSources.map(async (source) => {
      if (source === 'codemeta') return { source, found: true };
      try {
        const { data } = await api.get(
          `/datasource?source=${source}&recordId=${partyId}`,
        );
        return { source, found: data?.result?.length > 0 };
        // eslint-disable-next-line no-unused-vars
      } catch (error) {
        return { source, found: false, error: true };
      }
    });

    const results = await Promise.all(promises);

    const missing = results
      .filter((res) => !res.found && res.source !== 'codemeta')
      .map((res) => res.source);

    setParentPartyMissingSources(missing);
    setIsCheckingParentParty(false);
  };

  useEffect(() => {
    document.addEventListener('visibilitychange', checkParentPartySources);
    return () => {
      document.removeEventListener('visibilitychange', checkParentPartySources);
    };
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    const toastId = toast.loading('Speichervorgang wird gestartet...', {
      closeButton: true,
    });

    const progressReporter = {
      report: (message) => {
        toast.update(toastId, {
          render: message,
          isLoading: true,
          closeButton: true,
        });
      },
      success: (message) => {
        toast.update(toastId, {
          render: message,
          type: 'success',
          isLoading: false,
          autoClose: 4000,
          closeButton: true,
        });
      },
      error: (message) => {
        toast.update(toastId, {
          render: message,
          type: 'error',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      },
    };

    try {
      await saveParty(
        partyData,
        sourcesToHandle,
        users,
        progressReporter,
        data, // original data for contact merge,
        deployment?.tenant,
      );

      progressReporter.success('Alle Daten erfolgreich gespeichert!');

      if (handleClose) {
        setTimeout(() => handleClose(), 1500);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      progressReporter.error(error.message || 'Ein Fehler ist aufgetreten.');
    } finally {
      setIsSaving(false);
    }
  };

  const isUIBlocked =
    !!pendingMatchConfirmation || !!pendingImportConfirmation || alert.open;
  const isPerson = partyData?.partyType === 'PERSON';
  const isNewParty = !currentCodemetaParty?.id;
  const isLead = partyData
    ? ['NEW', 'PREQUALIFIED', 'QUALIFIED'].includes(
        getDisplayValue('leadStatus'),
      )
    : false;

  const weclappDisabled = useMemo(() => {
    if (partyData?.partyType !== 'PERSON') {
      return false;
    }
    const assignments = getDisplayValue('assignedOrganizations') || [];
    return (
      assignments.length > 0 && !assignments.some((org) => org.contact === true)
    );
  }, [partyData]);

  const disabledSaveBtn = useMemo(() => {
    if (isUIBlocked) return true;
    if (!partyData) return true;
    if (isPerson && partyData.email['edit']?.trim() === '') return true;

    return false;
  }, [isUIBlocked, isPerson, partyData]);

  if (!partyData) {
    return <div>Lade Party Manager...</div>;
  }

  const getCustomOptionLabel = (option) => {
    if (!option) return '';
    const name = `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim();
    return option.customerNumber ? `[${option.customerNumber}] ${name}` : name;
  };

  const renderCustomOption = (props, option) => {
    return (
      <Box component="li" {...props} key={option._id}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box>
            <Typography variant="body1" component="span">
              {getCustomOptionLabel(option)}
            </Typography>
            {option.email && (
              <Typography
                variant="body2"
                component="span"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                ({option.email})
              </Typography>
            )}
          </Box>
          {option.personCompany && (
            <Typography variant="caption" color="text.secondary">
              {option.personCompany}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const contextValue = {
    partyData,
    isUIBlocked,
    sourcesToHandle,
    contactWarnings,
    currentCodemetaParty,
    isPartyManagerLoading,
    isCheckingParentParty,
    parentPartyMissingSources,
    responsibleUserWarnings,
    setAlert,
    setPartyData,
    getDisplayValue,
    handleRoleChange,
    handleCycleSource,
    handleFieldChange,
    handleEmployeeChange,
    handleContactChange,
    handlePrimaryChange,
    getAvailableSources,
    getCustomOptionLabel,
    renderCustomOption,
    checkParentPartySources,
    handleTransferContactsToEdit,
    handleOrganizationAssignmentChange,
    handleOrganizationContactToggle,
    handleTransferOrganizationsToEdit,
    handleAddAddress,
    handleRemoveAddress,
    handleAddressPrimaryChange,
    handleAddressFieldChange,
    getAvailableAddressSources,
    handleAddressCycleSource,
    setIsPartyManagerLoading,
    transformArrayToStateStructure,
    organizationOptions,
    isSearchingOrgs,
    personOptions,
    isSearchingPersons,
    handleOrganizationSearch,
    handlePersonSearch,
    selectedOrgValue,
    selectedPersonValue,
  };
  // console.log(partyData);

  return (
    <PartyManagerContext.Provider value={contextValue}>
      <Box
        sx={{
          // border: '2px solid #ccc',
          padding: '16px',
          // borderRadius: '8px',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
        }}
      >
        <PartyHeader />
        <PartyAlert
          pendingMatchConfirmation={pendingMatchConfirmation}
          onConfirmMatch={handleConfirmMatch}
          onCancelMatch={handleCancelMatch}
          pendingImportConfirmation={pendingImportConfirmation}
          onConfirmImport={handleConfirmImport}
          onCancelImport={handleCancelImport}
          isPerson={isPerson}
          alert={alert}
          onCloseAlert={handleCloseAlert}
        />
        <fieldset
          disabled={isUIBlocked}
          style={{
            border: 'none',
            padding: 0,
            margin: 0,
            opacity: isUIBlocked ? 0.5 : 1,
          }}
        >
          <PartyTypeSelector disabled={!!currentCodemetaParty?.id || !!data} />
          {isPerson ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Stammdaten
              </Typography>
              <FormField
                fieldKey="email"
                label="E-Mail"
                isRequired={true}
                type={'email'}
              />
              <SalutationField label="Anrede" />
              <FormField fieldKey="firstName" label="Vorname" />
              <FormField fieldKey="lastName" label="Nachname" />
              <DateField
                fieldKey="birthDate"
                label="Geburtsdatum"
                maxDate={new Date()}
              />
              <PartyRoleField
                fieldKey="customerNumber"
                label="Knd.-Nr."
                roleKey="isCustomer"
                roleLabel="Privatkunde"
                checkboxRequired={true}
                editable={getDisplayValue('isCustomer')}
              />
              <CustomerNumberChecker
                isChecking={isCheckingPrivateCustomerNumber}
                duplicateCustomerNumbers={duplicateCustomerNumbers}
              />
              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Verantwortlich
              </Typography>
              <ResponsibleUserSelector label="Verantwortlich" />
              {!partyData.isCustomer[partyData.isCustomer.selected] && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 0.5, color: 'text.secondary' }}
                  >
                    Firmenzugehörigkeit
                  </Typography>
                  <OrganizationAssignmentSelector label="Zugeordnete Firmen" />
                </>
              )}

              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Kontaktdaten
              </Typography>
              <FormField fieldKey="phone" label="Haupttelefon" type="phone" />
              <FormField fieldKey="fax" label="Telefax" type="fax" />
              <FormField fieldKey="mobilePhone1" label="Mobil" type="phone" />
              <FormField fieldKey="mobilePhone2" label="Mobil 2" type="phone" />
              <FormField
                fieldKey="phoneHome"
                label="Privattelefon"
                type="phone"
              />
              <FormField fieldKey="website" label="Webseite" type="website" />

              {partyData.isCustomer[partyData.isCustomer.selected] && (
                <>
                  <ArrayField fieldKey="domains" label="Domains" />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mt: 0.5, color: 'text.secondary' }}
                      >
                        Addressen
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      {!isUIBlocked && (
                        <IconButton
                          onClick={handleAddAddress}
                          aria-label="Adresse hinzufügen"
                          size="small"
                          sx={{
                            color: 'success.dark',
                            borderRadius: 1,
                            mr: 1,
                            '&:hover': {
                              backgroundColor: 'success.main',
                              color: 'common.white',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'action.disabledBackground',
                            },
                          }}
                        >
                          <AddIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Box>
                    <PartyAddresses disabled={isUIBlocked} />
                  </Box>
                </>
              )}
              <TargetSystemSelector
                sourcesToHandle={sourcesToHandle}
                onSourceToggle={handleSourceToggle}
                setSourcesToHandle={setSourcesToHandle}
                weclappDisabled={weclappDisabled}
                isNewParty={isNewParty}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Stammdaten
              </Typography>
              <PartyRoleField
                fieldKey="customerNumber"
                label="Kd.-Nr."
                roleKey="isCustomer"
                roleLabel="Kunde"
                roleCheckboxDisabled={isLead}
              />
              <PartyRoleField
                fieldKey="supplierNumber"
                label="Lf.-Nr."
                roleKey="isSupplier"
                roleLabel="Lieferant"
              />
              <PartyRoleField
                fieldKey="leadStatus"
                label="Interessent"
                isLeadControl={true}
                leadStatusOptions={LEAD_STATUS.filter((item) => {
                  const customerNumber = getDisplayValue('customerNumber');
                  const leadStatus = getDisplayValue('leadStatus');
                  const isC = partyData.customerNumber.codemeta;
                  const isW = partyData.customerNumber.weclapp;

                  if (item.value === 'CONVERTED') {
                    if (isNewParty && leadStatus !== 'NONE' && (isC || isW))
                      return true;
                    if (isNewParty) return false;
                    return !customerNumber;
                  }
                  if (
                    item.value === 'NONE' &&
                    leadStatus !== 'NONE' &&
                    currentCodemetaParty &&
                    data
                  ) {
                    if (isC || isW) return false;
                  }
                  if (
                    item.value === 'NONE' &&
                    leadStatus !== 'NONE' &&
                    !currentCodemetaParty &&
                    !data
                  ) {
                    if (isC || isW) return false;
                  }
                  return item.value;
                })}
                disabled={
                  (!isNewParty &&
                    (!!getDisplayValue('customerNumber') ||
                      getDisplayValue('leadStatus') === 'CONVERTED')) ||
                  (currentCodemetaParty &&
                    ((getDisplayValue('leadStatus') === 'CONVERTED' &&
                      currentCodemetaParty?.leadStatus === 'CONVERTED') ||
                      getDisplayValue('leadStatus') === 'NONE'))
                }
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <SalutationField label="Anrede" />
                <FormField fieldKey="company" label="Firma" />
                <FormField fieldKey="company2" label="Firma-Zusatz" />
                <FormField fieldKey="referenceNumber" label="Referenznummer" />
                <ParentPartySelector
                  label="Firmenhauptsitz"
                  options={organizationOptions.filter(
                    (p) => p._id !== currentCodemetaParty?._id,
                  )}
                />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Verantwortlich
              </Typography>
              <ResponsibleUserSelector label="Mitarbeiter:in" />
              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Ansprechpersonen
              </Typography>
              <EmployeeSelector label="Kontakte" />
              <ContactSelector label="Ansprechpersonen" />
              <Typography
                variant="subtitle2"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                Kontaktdaten
              </Typography>
              <FormField fieldKey="email" label="E-Mail" type={'email'} />
              <FormField fieldKey="phone" label="Haupttelefon" type="phone" />
              <FormField fieldKey="fax" label="Telefax" type="fax" />
              <FormField fieldKey="website" label="Webseite" type="website" />
              <ArrayField fieldKey="departments" label="Abteilungen" />
              <ArrayField fieldKey="domains" label="Domains" />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 1.5, mb: 0.5, color: 'text.secondary' }}
                  >
                    Addressen
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {!isUIBlocked && (
                    <IconButton
                      onClick={handleAddAddress}
                      aria-label="Adresse hinzufügen"
                      size="small"
                      sx={{
                        color: 'success.dark',
                        borderRadius: 1,
                        mr: 1,
                        '&:hover': {
                          backgroundColor: 'success.main',
                          color: 'common.white',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'action.disabledBackground',
                        },
                      }}
                    >
                      <AddIcon fontSize="inherit" />
                    </IconButton>
                  )}
                </Box>
                <PartyAddresses disabled={isUIBlocked} />
                <TargetSystemSelector
                  sourcesToHandle={sourcesToHandle}
                  onSourceToggle={handleSourceToggle}
                  weclappDisabled={weclappDisabled}
                  isNewParty={isNewParty}
                />
              </Box>
            </Box>
          )}
          <LoadingButton
            onClick={handleSave}
            loading={isSaving}
            disabled={disabledSaveBtn || isSaving}
            sx={{ float: 'right' }}
            variant="outlined"
          >
            Speichern
          </LoadingButton>
        </fieldset>
      </Box>
    </PartyManagerContext.Provider>
  );
};

export default PartyManager;
